import React, { useState, useEffect } from 'react';
import Logo from '../../components/common/Logo';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Users, DollarSign, Calendar } from 'lucide-react';
import DoctorSidebar from '../../components/doctor/DoctorSidebar';
import AppointmentService from '../../services/appoinment.service';

const DoctorStatisticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    quickStats: {
      totalPatients: 0,
      totalRevenue: 0,
      monthlyGrowth: 0
    },
    monthlyAppointments: [],
    monthlyRevenue: [],
    patientTypeData: [],
    serviceDistribution: [],
    appointmentStatus: []
  });

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      
      // Recupera i dati degli appuntamenti dal servizio
      const appointments = await AppointmentService.getDoctorAppointments();
      
      // Genera le statistiche dai dati degli appuntamenti
      const statistics = generateStatistics(appointments);
      
      setStats(statistics);
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento delle statistiche:', err);
      setError('Non è stato possibile caricare le statistiche. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const generateStatistics = (appointments) => {
    // Assicurati che appointments sia un array
    const appointmentsArray = Array.isArray(appointments) 
      ? appointments 
      : (Array.isArray(appointments.data) ? appointments.data : []);
    
    // Filtra solo gli appuntamenti confermati
    const confirmedAppointments = appointmentsArray.filter(app => app.status === 'confirmed');

    if (confirmedAppointments.length === 0) {
      return {
        quickStats: {
          totalPatients: 0,
          totalRevenue: 0,
          monthlyGrowth: 0
        },
        monthlyAppointments: [],
        monthlyRevenue: [],
        patientTypeData: [],
        serviceDistribution: [],
        appointmentStatus: []
      };
    }

    // 1. Contare i pazienti unici (solo confermati)
    const uniquePatientIds = new Set(confirmedAppointments.map(app => app.patient_id));
    const totalPatients = uniquePatientIds.size;
    
    // 2. Calcolare il guadagno totale (solo confermati, usando "price")
    const totalRevenue = confirmedAppointments.reduce((sum, app) => sum + parseFloat(app.price || 0), 0);
    
    // 3. Raggruppare appuntamenti per mese (per grafici mensili)
    const appointmentsByMonth = {};
    const revenueByMonth = {};
    
    // Ottieni gli ultimi 6 mesi per assicurarti che tutti i mesi siano rappresentati
    const months = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = d.toLocaleString('it-IT', { month: 'short' });
      months.push(monthName);
      
      // Inizializza con valori zero
      appointmentsByMonth[monthName] = 0;
      revenueByMonth[monthName] = 0;
    }
    
    confirmedAppointments.forEach(app => {
      if (!app.date) return;
      
      const date = new Date(app.date);
      const month = date.toLocaleString('it-IT', { month: 'short' });
      
      // Incrementa il conteggio degli appuntamenti per questo mese
      if (months.includes(month)) {
        appointmentsByMonth[month] = (appointmentsByMonth[month] || 0) + 1;
        
        // Aggiungi il ricavo per questo mese (usando "price")
        revenueByMonth[month] = (revenueByMonth[month] || 0) + parseFloat(app.price || 0);
      }
    });
    
    // 4. Calcolare il tasso di completamento (appuntamenti completati / totali confermati)
    const completedAppointments = confirmedAppointments.filter(app => app.status === 'completed').length;
    const completionRate = confirmedAppointments.length > 0 
      ? Math.round((completedAppointments / confirmedAppointments.length) * 100) 
      : 0;
    
    // 5. Distribuzione degli stati degli appuntamenti (solo confermati)
    const statusCounts = {};
    const statusLabels = {
      'confirmed': 'Confermati',
      'completed': 'Completati',
      'cancelled': 'Annullati',
      'pending_payment': 'In attesa di pagamento',
      'paid': 'Pagati'
    };
    
    confirmedAppointments.forEach(app => {
      const statusLabel = statusLabels[app.status] || app.status;
      statusCounts[statusLabel] = (statusCounts[statusLabel] || 0) + 1;
    });
    
    // 6. Calcolare i nuovi pazienti vs pazienti ricorrenti (solo confermati)
    const appointmentCountByPatient = {};
    confirmedAppointments.forEach(app => {
      appointmentCountByPatient[app.patient_id] = (appointmentCountByPatient[app.patient_id] || 0) + 1;
    });
    
    const newPatients = Object.values(appointmentCountByPatient).filter(count => count === 1).length;
    const recurringPatients = Object.values(appointmentCountByPatient).filter(count => count > 1).length;
    
    // 7. Distribuzione dei servizi (solo confermati)
    const serviceCount = {};
    confirmedAppointments.forEach(app => {
      if (app.service_name) {
        serviceCount[app.service_name] = (serviceCount[app.service_name] || 0) + 1;
      }
    });
    
    // Converti i dati in formato utilizzabile per i grafici
    const monthlyAppointments = Object.entries(appointmentsByMonth).map(([name, count]) => ({
      name,
      appointments: count
    }));
    
    const monthlyRevenue = Object.entries(revenueByMonth).map(([name, revenue]) => ({
      name,
      revenue: Math.round(revenue)
    }));
    
    const patientTypeData = [
      { name: 'Nuovi', value: newPatients },
      { name: 'Ricorrenti', value: recurringPatients }
    ];
    
    const serviceDistribution = Object.entries(serviceCount).map(([name, count]) => ({
      name,
      count
    }));
    
    const appointmentStatus = Object.entries(statusCounts).map(([name, count]) => ({
      name,
      count
    }));
    
    // 8. Calcola la crescita mensile (semplificata)
    // Confrontiamo il mese corrente con quello precedente
    const monthsList = Object.keys(appointmentsByMonth);
    const currentMonthIndex = monthsList.length - 1;
    const previousMonthIndex = currentMonthIndex - 1;
    
    let monthlyGrowth = 0;
    if (currentMonthIndex >= 0 && previousMonthIndex >= 0) {
      const currentMonth = monthsList[currentMonthIndex];
      const previousMonth = monthsList[previousMonthIndex];
      
      const currentCount = appointmentsByMonth[currentMonth] || 0;
      const previousCount = appointmentsByMonth[previousMonth] || 0;
      
      if (previousCount > 0) {
        monthlyGrowth = Math.round(((currentCount - previousCount) / previousCount) * 100);
      } else if (currentCount > 0) {
        monthlyGrowth = 100; // Se il mese precedente era 0, ma questo mese abbiamo appuntamenti
      }
    }
    
    return {
      quickStats: {
        totalPatients,
        completionRate,
        totalRevenue: Math.round(totalRevenue),
        monthlyGrowth
      },
      monthlyAppointments,
      monthlyRevenue,
      patientTypeData,
      serviceDistribution,
      appointmentStatus
    };
};
    
  const COLORS = ['#8b5cf6', '#ddd6fe', '#c4b5fd', '#a78bfa', '#7c3aed'];
  
  if (loading) {
    return (
      <div className="flex min-h-screen">
        <div className="w-64 bg-white shadow-md">
          <div className="p-4">
            <Logo />
          </div>
          <DoctorSidebar />
        </div>
        <div className="flex-1 bg-[#FAF4EB] p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-600">Caricamento statistiche in corso...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <DoctorSidebar />
      </div>

      <div className="flex-1 bg-[#FAF4EB] p-8">
        <header className="mb-8">
          <h1 className="text-2xl font-bold">Statistiche</h1>
          <p className="text-gray-600">Analisi dettagliata della tua attività professionale</p>
        </header>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8">
            <p>{error}</p>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Pazienti Totali</p>
                <p className="text-2xl font-bold">{stats.quickStats.totalPatients}</p>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Fatturato Totale</p>
                <p className="text-2xl font-bold">{stats.quickStats.totalRevenue}€</p>
                {stats.quickStats.monthlyGrowth !== 0 && (
                  <p className={`text-sm ${stats.quickStats.monthlyGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {stats.quickStats.monthlyGrowth > 0 ? '+' : ''}{stats.quickStats.monthlyGrowth}% questo mese
                  </p>
                )}
              </div>
              <DollarSign className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Appointments Chart */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4">Appuntamenti Mensili</h2>
            {stats.monthlyAppointments.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.monthlyAppointments}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="appointments" fill="#8b5cf6" name="Appuntamenti" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <p className="text-gray-500">Nessun dato disponibile</p>
              </div>
            )}
          </div>

          {/* Revenue Chart */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4">Fatturato Mensile (€)</h2>
            {stats.monthlyRevenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#8b5cf6" 
                    name="Fatturato"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <p className="text-gray-500">Nessun dato disponibile</p>
              </div>
            )}
          </div>

          {/* Patient Type Distribution */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4">Distribuzione Pazienti</h2>
            {stats.patientTypeData.some(item => item.value > 0) ? (
              <div className="flex items-center justify-center">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stats.patientTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                      label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {stats.patientTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <p className="text-gray-500">Nessun dato disponibile</p>
              </div>
            )}
          </div>

          {/* Service Distribution */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4">Distribuzione Servizi</h2>
            {stats.serviceDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={stats.serviceDistribution} 
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#8b5cf6" name="Numero di appuntamenti" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <p className="text-gray-500">Nessun dato disponibile</p>
              </div>
            )}
          </div>

          {/* Performance Metrics */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4">Servizi più richiesti</h2>
            <div className="space-y-4">
              {stats.serviceDistribution.slice(0, 3).map((service, index) => (
                <div key={index}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">{service.name}</span>
                    <span className="text-sm font-medium">{service.count} appuntamenti</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, service.count * 10)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
              {stats.serviceDistribution.length === 0 && (
                <p className="text-gray-500 text-center">Nessun servizio disponibile</p>
              )}
            </div>
          </div>

          {/* Appointment Status */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4">Prossimi Appuntamenti</h2>
            {stats.appointmentStatus.length > 0 ? (
              <div className="space-y-4">
                {stats.monthlyAppointments.filter(month => month.appointments > 0).slice(0, 3).map((month, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                      <Calendar className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{month.name}</p>
                      <p className="text-xs text-gray-500">{month.appointments} appuntamenti</p>
                    </div>
                  </div>
                ))}
                {stats.monthlyAppointments.every(month => month.appointments === 0) && (
                  <p className="text-gray-500 text-center">Nessun appuntamento programmato</p>
                )}
              </div>
            ) : (
              <div className="text-center p-4">
                <p className="text-gray-500">Nessun appuntamento disponibile</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorStatisticsPage;